// Shared syntax highlighting — used by both renderer CodeBlock and editor InlineCodeBlock

const KEYWORD_PATTERNS: Record<string, RegExp> = {
  javascript: /\b(const|let|var|function|return|if|else|for|while|class|import|export|from|default|async|await|new|this|try|catch|throw|typeof|instanceof)\b/g,
  python: /\b(def|class|return|if|elif|else|for|while|import|from|as|with|try|except|raise|in|not|and|or|is|True|False|None|self|lambda|yield)\b/g,
  ruby: /\b(def|end|class|module|if|elsif|else|unless|do|while|for|return|require|include|attr_accessor|attr_reader|puts|nil|true|false|self|yield|block_given\?)\b/g,
  html: /(<\/?[a-zA-Z][a-zA-Z0-9]*|>|\/>)/g,
  css: /\b(display|margin|padding|color|background|border|font|width|height|position|flex|grid|none|auto|inherit|solid|center)\b/g,
  bash: /\b(if|then|else|elif|fi|for|while|do|done|case|esac|function|return|exit|echo|export|source|alias|cd|ls|grep|awk|sed|cat|rm|cp|mv|mkdir|chmod|sudo|apt|npm|yarn|pip|git|docker|curl|wget)\b/g,
}

export function highlightCodeToHtml(code: string, language: string): string {
  const lines = code.split('\n')
  return lines.map((line, lineIdx) => {
    const commentRegex = language === 'python' || language === 'ruby' || language === 'bash' ? /#.*$/ : language === 'html' ? /<!--[\s\S]*?-->/ : /\/\/.*$|\/\*[\s\S]*?\*\//
    const commentMatch = line.match(commentRegex)
    let commentStart = commentMatch ? line.indexOf(commentMatch[0]) : -1
    if (commentStart === -1) commentStart = line.length

    const beforeComment = line.slice(0, commentStart)
    const commentPart = line.slice(commentStart)

    const tokens: { text: string; type: 'plain' | 'string' | 'comment' }[] = []
    let cursor = 0
    const strRegex = /(["'`])(?:(?!\1|\\).|\\.)*\1/g
    let strMatch: RegExpExecArray | null
    while ((strMatch = strRegex.exec(beforeComment)) !== null) {
      if (strMatch.index > cursor) {
        tokens.push({ text: beforeComment.slice(cursor, strMatch.index), type: 'plain' })
      }
      tokens.push({ text: strMatch[0], type: 'string' })
      cursor = strMatch.index + strMatch[0].length
    }
    if (cursor < beforeComment.length) {
      tokens.push({ text: beforeComment.slice(cursor), type: 'plain' })
    }
    if (commentPart) {
      tokens.push({ text: commentPart, type: 'comment' })
    }

    const kwRegex = KEYWORD_PATTERNS[language]
    let html = ''
    for (const token of tokens) {
      if (token.type === 'plain' && kwRegex) {
        const kwRegexCopy = new RegExp(kwRegex.source, 'g')
        let lastIdx = 0
        let kwMatch: RegExpExecArray | null
        while ((kwMatch = kwRegexCopy.exec(token.text)) !== null) {
          if (kwMatch.index > lastIdx) {
            html += escapeHtml(token.text.slice(lastIdx, kwMatch.index))
          }
          html += `<span style="color:#c084fc;font-weight:500">${escapeHtml(kwMatch[0])}</span>`
          lastIdx = kwMatch.index + kwMatch[0].length
        }
        if (lastIdx < token.text.length) {
          html += escapeHtml(token.text.slice(lastIdx))
        }
      } else if (token.type === 'string') {
        html += `<span style="color:#4ade80">${escapeHtml(token.text)}</span>`
      } else if (token.type === 'comment') {
        html += `<span style="color:#64748b;font-style:italic">${escapeHtml(token.text)}</span>`
      } else {
        html += escapeHtml(token.text)
      }
    }

    return `<div>${html || ' '}</div>`
  }).join('')
}

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}
